import React, { useState, useEffect, useRef, useCallback } from "react";
import { siteData } from "../data/mockData";
import { ChevronLeft, ChevronRight } from "lucide-react";

const GallerySection = () => {
  const { gallery, testimonials } = siteData;
  const [activeReview, setActiveReview] = useState(0);
  const [galleryIndex, setGalleryIndex] = useState(gallery.length);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isGalleryHovered, setIsGalleryHovered] = useState(false);
  const galleryIntervalRef = useRef(null);
  const reviewIntervalRef = useRef(null);

  // 3 copies for infinite loop: [clone-end, ...real, clone-start]
  const extendedGallery = [...gallery, ...gallery, ...gallery];

  // Auto-rotate testimonials
  useEffect(() => {
    reviewIntervalRef.current = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(reviewIntervalRef.current);
  }, [testimonials.length]);

  // Auto-scroll gallery
  const startGalleryScroll = useCallback(() => {
    if (galleryIntervalRef.current) clearInterval(galleryIntervalRef.current);
    galleryIntervalRef.current = setInterval(() => {
      setGalleryIndex((prev) => prev + 1);
    }, 3000);
  }, []);

  useEffect(() => {
    if (!isGalleryHovered) {
      startGalleryScroll();
    } else {
      if (galleryIntervalRef.current) clearInterval(galleryIntervalRef.current);
    }
    return () => {
      if (galleryIntervalRef.current) clearInterval(galleryIntervalRef.current);
    };
  }, [isGalleryHovered, startGalleryScroll]);

  // Seamless infinite loop reset
  const handleTransitionEnd = useCallback(() => {
    if (galleryIndex >= gallery.length * 2) {
      setIsTransitioning(false);
      setGalleryIndex(gallery.length);
    } else if (galleryIndex < gallery.length) {
      setIsTransitioning(false);
      setGalleryIndex(gallery.length * 2 - 1);
    }
  }, [galleryIndex, gallery.length]);

  // Re-enable transition after instant reset
  useEffect(() => {
    if (!isTransitioning) {
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isTransitioning]);

  // Manual navigation
  const goNext = () => {
    setGalleryIndex((prev) => prev + 1);
    // Reset auto-scroll timer
    if (!isGalleryHovered) startGalleryScroll();
  };

  const goPrev = () => {
    setGalleryIndex((prev) => prev - 1);
    if (!isGalleryHovered) startGalleryScroll();
  };

  const handleReviewClick = (index) => {
    setActiveReview(index);
    clearInterval(reviewIntervalRef.current);
    reviewIntervalRef.current = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % testimonials.length);
    }, 6000);
  };

  return (
    <section className="relative">
      {/* Testimonials - Teal section with background image overlay */}
      <div
        className="relative w-full overflow-hidden"
        style={{ minHeight: "480px" }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${gallery[2]})`,
          }}
        />
        {/* Teal overlay */}
        <div className="absolute inset-0 bg-[#1CA0B9]/85" />

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-16 py-16 md:py-20">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20 items-start">
            {/* Left - Title */}
            <div className="flex-shrink-0">
              <h2 className="font-poppins font-bold text-3xl md:text-[2.5rem] text-white leading-tight">
                Testimonials
              </h2>
            </div>

            {/* Right - Active review */}
            <div className="flex-1">
              <div className="min-h-[200px]">
                <p className="font-poppins text-white text-base md:text-lg leading-relaxed mb-6 transition-opacity duration-500">
                  &ldquo;{testimonials[activeReview].text}&rdquo;
                </p>
                <p className="font-poppins font-semibold text-white text-base">
                  &ndash; {testimonials[activeReview].author},{" "}
                  {testimonials[activeReview].location}
                </p>
              </div>

              {/* Reviewer profile photos */}
              <div className="flex items-center gap-4 mt-8">
                {testimonials.map((review, index) => (
                  <button
                    key={index}
                    onClick={() => handleReviewClick(index)}
                    className={`rounded-full overflow-hidden border-[3px] transition-all duration-300 ${
                      activeReview === index
                        ? "border-[#D1D776] w-14 h-14 shadow-lg scale-110"
                        : "border-white/50 w-12 h-12 opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={review.avatar}
                      alt={review.author}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery - Overlapping white card with arrows */}
      <div className="relative -mt-8 z-20 px-4 md:px-12">
        <div
          className="max-w-[1200px] mx-auto bg-white rounded-2xl shadow-xl p-4 md:p-6 relative group"
          onMouseEnter={() => setIsGalleryHovered(true)}
          onMouseLeave={() => setIsGalleryHovered(false)}
        >
          {/* Left Arrow */}
          <button
            onClick={goPrev}
            className="absolute left-2 md:-left-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1CA0B9] hover:bg-[#1CA0B9] hover:text-white transition-colors duration-200 border border-gray-200"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={goNext}
            className="absolute right-2 md:-right-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1CA0B9] hover:bg-[#1CA0B9] hover:text-white transition-colors duration-200 border border-gray-200"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Carousel Track */}
          <div className="overflow-hidden rounded-lg">
            <div
              className="flex"
              onTransitionEnd={handleTransitionEnd}
              style={{
                transform: `translateX(-${galleryIndex * 50}%)`,
                transition: isTransitioning
                  ? "transform 0.7s ease-in-out"
                  : "none",
              }}
            >
              {extendedGallery.map((img, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-[50%] px-2"
                >
                  <div className="aspect-[16/10] overflow-hidden rounded-lg">
                    <img
                      src={img}
                      alt={`Gallery ${(index % gallery.length) + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Spacing below gallery card */}
      <div className="h-12 md:h-16" />
    </section>
  );
};

export default GallerySection;
